import { useState, useCallback, useEffect } from 'react'
import dayjs, { type Dayjs } from 'dayjs'

function identity<T>(x: T): T {
  return x
}

type Options = {
  expires?: number | Dayjs
  path?: string
  secure?: boolean
  domain?: string
}

const setCookie = (name: string, value: string, options: Options) => {
  const expires = (
    options.expires && dayjs.isDayjs(options.expires) ? options.expires : dayjs().add(options.expires || 7, 'day')
  )
    .toDate()
    .toUTCString()

  const path = options.path || '/'

  const secure = options.secure ? 'secure' : ''
  const domain = options.domain ? `domain=${options.domain}` : ''
  const cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `expires=${expires}`,
    `path=${path}`,
    'samesite=lax',
    secure,
    domain,
  ]
    .filter(Boolean)
    .join(';')

  document.cookie = cookie
}

const getCookie = (name: string): string | null => {
  const result = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('='))
    .find(([key]) => key === name)

  if (result) {
    return decodeURIComponent(result[1])
  }
  return null
}

type EventDetail<T> = {
  key: string
  value: T
}

type ParserOptions<T> =
  | {
      raw: true
      cookieOptions?: Options
    }
  | {
      raw: false
      serializer: (value: T) => string
      deserializer: (value: string) => T
      cookieOptions?: Options
    }

export function useCookie<T>(
  key: string,
  initialValue: T,
  options: ParserOptions<T>
): [T, (value: T, options: Options) => void] {
  // eslint-disable-next-line no-nested-ternary
  const serializer = options ? (options.raw ? identity : options.serializer) : JSON.stringify
  // eslint-disable-next-line no-nested-ternary
  const deserializer = options ? (options.raw ? identity : options.deserializer) : JSON.parse
  const { cookieOptions = {} } = options

  const [item, setItem] = useState<T>(() => {
    const value = getCookie(key)

    if (value && value !== 'undefined') {
      return deserializer(value)
    }
    setCookie(key, serializer(initialValue), cookieOptions)
    return initialValue
  })

  useEffect(() => {
    const onCookieChange = (e: CustomEvent<EventDetail<T>>) => {
      if (e.detail.key === key) {
        setItem(e.detail.value)
      }
    }

    document.addEventListener('cookiechange', onCookieChange as EventListener)

    return () => {
      document.removeEventListener('cookiechange', onCookieChange as EventListener)
    }
  }, [key, setItem])

  const decoratedSetItem = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (value: T, options: Options) => {
      setItem(value)
      setCookie(key, serializer(value), options)

      document.dispatchEvent(
        new CustomEvent<EventDetail<T>>('cookiechange', {
          detail: {
            key,
            value,
          },
        })
      )
    },
    [key, setItem, serializer]
  )

  return [item, decoratedSetItem]
}
